import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User as UserIcon, Mail, Phone, Calendar, Loader2, Save, X } from "lucide-react";
import { formatDateLong } from "@/lib/utils";
import { formatPhoneNumber } from "@/lib/phone-utils";
import type { User } from "@/api/generated/client/models/User";
import type { UpdateUserForm } from "@/schemas/user";

interface ProfileInformationCardProps {
  user: User | null | undefined;
  isEditing: boolean;
  formData: UpdateUserForm;
  isPending: boolean;
  onFormDataChange: (data: UpdateUserForm) => void;
  onEdit: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onCancel: () => void;
}

export function ProfileInformationCard({
  user,
  isEditing,
  formData,
  isPending,
  onFormDataChange,
  onEdit,
  onSubmit,
  onCancel,
}: ProfileInformationCardProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <UserIcon className="h-5 w-5" />
              Profile Information
            </CardTitle>
            <CardDescription>
              Manage your personal information and contact details
            </CardDescription>
          </div>
          {!isEditing && (
            <Button onClick={onEdit} variant="outline">
              Edit Profile
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-6">
          {/* Email (Read-only) */}
          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              value={user?.email || ""}
              disabled
              className="bg-muted"
            />
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Full Name
            </Label>
            <Input
              id="name"
              type="text"
              value={formData.name || ""}
              onChange={(e) => onFormDataChange({ ...formData, name: e.target.value })}
              disabled={!isEditing}
              placeholder="Enter your full name"
            />
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Phone Number
            </Label>
            <Input
              id="phone"
              type="tel"
              value={isEditing ? (formData.phone || "") : formatPhoneNumber(user?.phone)}
              onChange={(e) => onFormDataChange({ ...formData, phone: e.target.value })}
              disabled={!isEditing}
              placeholder="+1 (555) 000-0000"
            />
          </div>

          {/* Role (Read-only) */}
          <div className="space-y-2">
            <Label htmlFor="role">
              Role
            </Label>
            <Input
              id="role"
              type="text"
              value={user?.role || ""}
              disabled
              className="bg-muted capitalize"
            />
          </div>

          {/* Member Since (Read-only) */}
          <div className="space-y-2">
            <Label htmlFor="createdAt" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Member Since
            </Label>
            <Input
              id="createdAt"
              type="text"
              value={user?.createdAt ? formatDateLong(user.createdAt) : ""}
              disabled
              className="bg-muted"
            />
          </div>

          {/* Action Buttons */}
          {isEditing && (
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={isPending}
                className="flex-1"
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isPending}
                className="flex-1"
              >
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
